if (!customElements.get('product-card-enriched')) {
  const enrichedCardCart = {
    lines: new Map(),
    listeners: new Set(),
    pending: new Set(),
    loaded: false,
    ready: null,

    ensure() {
      this.subscribe();
      if (!this.ready) this.ready = this.fetchCart();
      return this.ready;
    },

    subscribe() {
      if (this.subscribed || !window.FoxTheme?.pubsub) return;
      this.subscribed = true;
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cart?.items) this.applyCart(event.cart);
      });
    },

    async fetchCart() {
      try {
        const response = await fetch(`${FoxTheme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();
        this.applyCart(cart);
      } catch (error) {
        console.warn('ProductCardEnriched: cart sync failed', error);
        this.loaded = true;
        this.notify();
      }
    },

    applyCart(cart) {
      const lines = new Map();
      (cart.items || []).forEach((item) => {
        if (item.selling_plan_allocation) return;
        const id = Number(item.variant_id);
        const current = lines.get(id);
        if (!current) {
          lines.set(id, {
            quantity: item.quantity,
            key: item.key,
            lineQuantity: item.quantity,
          });
        } else {
          current.quantity += item.quantity;
        }
      });
      this.lines = lines;
      this.loaded = true;
      this.notify();
    },

    get(variantId) {
      return this.lines.get(Number(variantId)) || { quantity: 0, key: null, lineQuantity: 0 };
    },

    onChange(listener) {
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
    },

    notify() {
      this.listeners.forEach((listener) => listener());
    },

    isPending(variantId) {
      return this.pending.has(Number(variantId));
    },

    async changeLine(variantId, key, quantity, restoreFocus) {
      const id = Number(variantId);
      if (!key || this.pending.has(id) || !window.FoxTheme?.routes?.cart_change_url) return null;

      this.pending.add(id);
      this.notify();

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      try {
        const response = await fetch(FoxTheme.routes.cart_change_url, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({
            id: key,
            quantity,
            sections: sectionsToBundle,
          }),
        });
        const cart = await response.json();
        if (cart.status || cart.errors || !cart.items) {
          throw new Error(cart.description || cart.message || 'Cart update failed');
        }

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });

        const drawer = document.querySelector('cart-drawer');
        if (drawer && !drawer.open && restoreFocus && typeof restoreFocus.focus === 'function') {
          requestAnimationFrame(() => restoreFocus.focus());
        }

        return cart;
      } finally {
        this.pending.delete(id);
        this.notify();
      }
    },
  };

  class ProductCardEnriched extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this.selectedVariantId = null;
      this.mediaIndex = 0;
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }

    connectedCallback() {
      this.parseData();
      if (!this.data) return;

      this.imageEl = this.querySelector('[data-product-card-image]');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.weightEl = this.querySelector('[data-variant-weight]');
      this.priceContainer = this.querySelector('[data-price-container]');
      this.multibuyBadge = this.querySelector('[data-multibuy-badge]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.atcRoot = this.querySelector('.product-card__enriched-atc');
      this.atcTrigger = this.querySelector('[data-atc-trigger]');
      this.cartQtyCount = this.querySelector('[data-cart-qty-count]');
      this.cartQtyMinus = this.querySelector('[data-cart-qty-minus]');
      this.cartQtyPlus = this.querySelector('[data-cart-qty-plus]');
      this.variantTiles = this.querySelectorAll('.pcard-variant-tiles__tile');
      this.prevBtn = this.querySelector('[data-media-prev]');
      this.nextBtn = this.querySelector('[data-media-next]');

      this.bisTrigger = this.querySelector('[data-bis-trigger]');
      this.bisPanel = this.querySelector('[data-bis-panel]');
      this.bisForm = this.querySelector('[data-bis-form]');
      this.bisEmail = this.querySelector('[data-bis-email]');
      this.bisSubmit = this.querySelector('[data-bis-submit]');
      this.bisSubmitText = this.querySelector('[data-bis-submit-text]');

      const initialVariant = this.getVariant(this.data.variants.find((variant) => variant.available)?.id || this.data.variants[0]?.id);
      if (initialVariant) {
        this.selectVariant(initialVariant.id, false);
      }

      this.variantTiles.forEach((tile) => {
        tile.addEventListener('click', () => {
          const variantId = Number(tile.dataset.variantId);
          if (!variantId) return;
          this.selectVariant(variantId, true);
        });
      });

      if (this.dataset.showImageNav === 'true' && this.prevBtn && this.nextBtn) {
        this.prevBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.shiftMedia(-1);
        });
        this.nextBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.shiftMedia(1);
        });
      }

      if (this.dataset.showQuickAdd === 'true' && this.atcRoot) {
        this.unsubscribeCart = enrichedCardCart.onChange(() => this.syncCartControl());
        enrichedCardCart.ensure().then(() => this.syncCartControl());

        this.cartQtyMinus?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.adjustCartQuantity(-1);
        });
        this.cartQtyPlus?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.adjustCartQuantity(1);
        });
      }

      if (this.bisTrigger && this.bisPanel) {
        this.bisTrigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleBisPanel();
        });

        if (this.bisForm) {
          this.bisForm.addEventListener('submit', (event) => {
            event.preventDefault();
            this.submitBisForm();
          });
        }

        document.addEventListener('click', this.handleDocumentClick);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      if (this.unsubscribeCart) this.unsubscribeCart();
    }

    parseData() {
      const dataEl = this.querySelector('[data-product-card-data]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.warn('ProductCardEnriched: invalid product data', error);
      }
    }

    getVariant(variantId) {
      return this.data?.variants?.find((variant) => variant.id === Number(variantId));
    }

    selectVariant(variantId, updateMediaIndex) {
      const variant = this.getVariant(variantId);
      if (!variant) return;

      this.selectedVariantId = variant.id;

      this.variantTiles.forEach((tile) => {
        const isSelected = Number(tile.dataset.variantId) === variant.id;
        tile.classList.toggle('is-selected', isSelected);
        tile.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = !variant.available;
      }

      if (updateMediaIndex && this.data.media?.length) {
        if (variant.featuredMediaId) {
          const mediaIndex = this.data.media.findIndex((item) => item.id === variant.featuredMediaId);
          if (mediaIndex >= 0) {
            this.mediaIndex = mediaIndex;
          }
        } else {
          this.mediaIndex = 0;
        }

        this.renderMedia();
      }
      this.updateWeight(variant);
      this.updateDisplayedPrice();
      this.updateMultibuyBadge(variant);

      if (this.atcTrigger) {
        this.atcTrigger.disabled = !variant.available;
      }
      this.syncCartControl();
    }

    renderMedia() {
      if (!this.imageEl || !this.data.media?.length) return;

      const media = this.data.media[this.mediaIndex] || this.data.media[0];
      if (!media) return;

      this.imageEl.innerHTML = `<img src="${media.src}" alt="${this.escapeHtml(media.alt || '')}" loading="lazy" decoding="async" class="motion-reduce" style="width:100%;height:100%;object-fit:cover;">`;
    }

    shiftMedia(direction) {
      if (!this.data.media?.length) return;
      this.mediaIndex = (this.mediaIndex + direction + this.data.media.length) % this.data.media.length;
      this.renderMedia();
    }

    updateWeight(variant) {
      if (!this.weightEl) return;

      const valueEl = this.weightEl.querySelector('[data-variant-weight-value]');
      const weight = variant.weight;
      const hasWeight = weight !== null && weight !== undefined && String(weight).trim() !== '';

      this.weightEl.hidden = !hasWeight;
      if (valueEl) {
        valueEl.textContent = hasWeight ? String(weight) : '';
      }
    }

    updateMultibuyBadge(variant) {
      if (!this.multibuyBadge) return;

      const hasMultibuy = Boolean(
        variant.hasMultibuy ||
          variant.quantityPriceBreaks?.length ||
          (this.data.tagEligible && this.data.globalDiscountTiers?.length)
      );
      const defaultText = this.multibuyBadge.dataset.defaultText || 'BUY MORE & SAVE';

      this.multibuyBadge.dataset.variantId = String(variant.id);
      this.multibuyBadge.dataset.priceCents = String(variant.price);

      const applyDefault = () => {
        this.multibuyBadge.textContent = defaultText;
        this.multibuyBadge.hidden = !hasMultibuy;
        delete this.multibuyBadge.dataset.autoDiscountActive;
      };

      if (
        this.multibuyBadge.hasAttribute('data-auto-discount-badge') &&
        window.FoxTheme?.autoDiscountPrice?.refreshBadge
      ) {
        FoxTheme.autoDiscountPrice
          .refreshBadge(this.multibuyBadge, {
            variantId: variant.id,
            priceCents: variant.price,
            prefetchVariants: (this.data?.variants || [])
              .filter((item) => item?.available !== false)
              .map((item) => ({
                variantId: item.id,
                priceCents: item.price,
              })),
            fallbackVisible: hasMultibuy,
            fallbackText: defaultText,
          })
          .catch(() => applyDefault());
        return;
      }

      applyDefault();
    }

    getPriceForQuantity(variant, quantity) {
      const basePrice = variant.price;

      const breaks = [...(variant.quantityPriceBreaks || [])].sort(
        (a, b) => b.minimumQuantity - a.minimumQuantity
      );
      for (const priceBreak of breaks) {
        if (quantity >= priceBreak.minimumQuantity) {
          return priceBreak.price;
        }
      }

      if (this.data.tagEligible && this.data.globalDiscountTiers?.length) {
        const tiers = [...this.data.globalDiscountTiers].sort(
          (a, b) => b.minimumQuantity - a.minimumQuantity
        );
        for (const tier of tiers) {
          if (quantity >= tier.minimumQuantity) {
            return Math.round(basePrice * (1 - tier.percentOff / 100));
          }
        }
      }

      return basePrice;
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
      }

      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
      });
    }

    updateDisplayedPrice() {
      if (!this.priceContainer) return;

      const variant = this.getVariant(this.selectedVariantId);
      if (!variant) return;

      const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
      const basePrice = variant.price;
      const compareAtPrice = variant.compareAtPrice;
      const unitPrice = this.getPriceForQuantity(variant, quantity);

      // Prefer native compare-at when present; fall back to base price for tiered discounts.
      let comparePrice = null;
      if (compareAtPrice && compareAtPrice > unitPrice) {
        comparePrice = compareAtPrice;
      } else if (unitPrice < basePrice) {
        comparePrice = basePrice;
      }

      const isOnSale = comparePrice !== null && comparePrice > unitPrice;

      const fPrice = this.priceContainer.querySelector('.f-price');
      if (!fPrice) return;

      fPrice.classList.toggle('f-price--on-sale', isOnSale);

      const regularPriceEl = fPrice.querySelector('.f-price__regular .f-price-item--regular');
      if (regularPriceEl) {
        regularPriceEl.textContent = this.formatMoney(isOnSale ? comparePrice : unitPrice);
      }

      if (isOnSale) {
        const salePriceEl = fPrice.querySelector('.f-price__sale .f-price-item--sale');
        if (salePriceEl) salePriceEl.textContent = this.formatMoney(unitPrice);

        const compareEl = fPrice.querySelector('.f-price__sale .f-price-item--regular');
        if (compareEl) compareEl.innerHTML = `<s>${this.formatMoney(comparePrice)}</s>`;
      }
    }

    syncCartControl() {
      if (!this.atcRoot || !enrichedCardCart.loaded) return;

      const entry = enrichedCardCart.get(this.selectedVariantId);
      const inCart = entry.quantity > 0;
      this.atcRoot.classList.toggle('is-in-cart', inCart);
      if (this.cartQtyCount) this.cartQtyCount.textContent = String(entry.quantity);

      const pending = enrichedCardCart.isPending(this.selectedVariantId);
      if (this.cartQtyMinus) this.cartQtyMinus.disabled = pending || !entry.key;
      if (this.cartQtyPlus) this.cartQtyPlus.disabled = pending || !entry.key;
    }

    adjustCartQuantity(delta) {
      const entry = enrichedCardCart.get(this.selectedVariantId);
      if (!entry.key || enrichedCardCart.isPending(this.selectedVariantId)) return;

      const next = Math.max(0, entry.lineQuantity + delta);
      const restoreFocus = next === 0 ? this.atcTrigger : delta > 0 ? this.cartQtyPlus : this.cartQtyMinus;
      enrichedCardCart.changeLine(this.selectedVariantId, entry.key, next, restoreFocus).catch((error) => {
        console.warn('ProductCardEnriched: quantity update failed', error);
        this.syncCartControl();
      });
    }

    handleDocumentClick(event) {
      if (this.contains(event.target)) return;
      if (this.classList.contains('is-bis-open')) this.toggleBisPanel(false);
    }

    toggleBisPanel(forceOpen) {
      const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : !this.classList.contains('is-bis-open');
      this.classList.toggle('is-bis-open', shouldOpen);
      if (this.bisTrigger) this.bisTrigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      if (shouldOpen && this.bisEmail) {
        requestAnimationFrame(() => this.bisEmail.focus());
      }
    }

    getKlaviyoCompanyId() {
      if (this._klaviyoCompanyId) return this._klaviyoCompanyId;
      const script = document.querySelector('script[src*="klaviyo.com"][src*="company_id"]');
      if (script) {
        const match = script.src.match(/company_id=([A-Za-z0-9]+)/);
        if (match) {
          this._klaviyoCompanyId = match[1];
          return this._klaviyoCompanyId;
        }
      }
      return null;
    }

    async submitBisForm() {
      if (!this.bisEmail || !this.bisSubmitText) return;
      const email = this.bisEmail.value.trim();
      if (!email || !this.bisEmail.checkValidity()) {
        this.bisEmail.reportValidity();
        return;
      }

      const companyId = this.getKlaviyoCompanyId();
      if (!companyId) {
        console.warn('ProductCardEnriched: Klaviyo company ID not found');
        return;
      }

      const variantId = this.selectedVariantId || this.data?.variants?.[0]?.id;
      if (!variantId) return;

      this.classList.add('is-bis-submitting');
      const defaultLabel = this.bisSubmitText.innerHTML;
      this.bisSubmitText.innerHTML = '<span class="product-card__enriched-bis-submit-label">Sending...</span>';

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: '2024-10-15',
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email },
                    },
                  },
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );

        this.classList.remove('is-bis-submitting');

        if (response.ok || response.status === 202) {
          this.classList.add('is-bis-success');
          this.bisSubmitText.innerHTML = '<span class="product-card__enriched-bis-submit-label">Subscribed!</span>';
          setTimeout(() => {
            this.classList.remove('is-bis-success');
            this.toggleBisPanel(false);
            this.bisSubmitText.innerHTML = defaultLabel;
            this.bisEmail.value = '';
          }, 2000);
        } else {
          this.bisSubmitText.innerHTML = '<span class="product-card__enriched-bis-submit-label">Error</span>';
          setTimeout(() => {
            this.bisSubmitText.innerHTML = defaultLabel;
          }, 2000);
        }
      } catch (err) {
        console.warn('ProductCardEnriched: BIS subscription failed', err);
        this.classList.remove('is-bis-submitting');
        this.bisSubmitText.innerHTML = '<span class="product-card__enriched-bis-submit-label">Error</span>';
        setTimeout(() => {
          this.bisSubmitText.innerHTML = defaultLabel;
        }, 2000);
      }
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('product-card-enriched', ProductCardEnriched);
}
